/**
 * @fileoverview Defines the footer section of the application.
 *
 * This component renders a fixed footer at the bottom of the screen that displays
 * social media links and copyright information. It has a responsive design that
 * adapts its layout for mobile and desktop views.
 */

import {
  FacebookIcon,
  InstagramIcon,
  TwitterIcon,
  YoutubeIcon,
} from "lucide-react";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

/**
 * The footer component for the application.
 * It displays social media links and copyright information in a responsive layout.
 * On mobile, it shows the content in two rows, while on desktop it displays
 * everything in a single row with the copyright on the left and social links on the right.
 * @returns {JSX.Element} The rendered footer component.
 */
export const FooterSection = (): JSX.Element => {
  const { t } = useTranslation();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const currentYear = new Date().getFullYear();
  
  // Footer shows on all viewports; no desktop hide

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 480;
  const isTablet = windowWidth >= 480 && windowWidth < 1024;
  const isDesktop = windowWidth >= 1024;

  /**
   * Array of social media links with their respective icons, URLs, and brand colors.
   * Each link includes accessibility attributes and hover effects.
   * Updated with proper X logo and working URLs.
   */
  const socialLinks = [
    { 
      icon: <FacebookIcon size={14} />, 
      href: "https://www.facebook.com/spinnergames.fun", 
      ariaLabel: "Facebook",
      bgColor: "#1877F2", // Facebook blue
      iconColor: "#FFFFFF"
    },
    { 
      icon: <InstagramIcon size={14} />, 
      href: "https://www.instagram.com/spinnergames.fun", 
      ariaLabel: "Instagram",
      bgColor: "#E4405F", // Instagram pink
      iconColor: "#FFFFFF"
    },
    { 
      icon: <YoutubeIcon size={14} />, 
      href: "https://www.youtube.com/@SpinnerGamesFun", 
      ariaLabel: "YouTube",
      bgColor: "#FF0000", // YouTube red
      iconColor: "#FFFFFF"
    },
    { 
      icon: (
        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" transform="scale(1.2) translate(-2, -2)"/>
        </svg>
      ), 
      href: "https://x.com/SpinnerGamesFun", 
      ariaLabel: "X (Twitter)",
      bgColor: "#000000", // X black
      iconColor: "#FFFFFF",
      borderColor: "#374151" // Dark grey border for X
    },
    { 
      icon: (
        <img src="/images/tiktok.png" alt="TikTok" className="h-3.5 w-3.5" style={{ filter: 'brightness(0) invert(1)' }} />
      ), 
      href: "https://www.tiktok.com/@spinner.games", 
      ariaLabel: "TikTok",
      bgColor: "#000000", // TikTok black
      iconColor: "#FFFFFF",
      borderColor: "#374151" // Dark grey border for TikTok
    },
  ];

  return (
    <footer className="w-full bg-[#1e2434] text-white relative" data-testid="footer-section">
      {/* Standard Mobile layout: Social on top, copyright below */}
      {isMobile && (
        <div className="w-full py-2">
          <div className="flex flex-col items-center mb-2 w-full">
            <div className="flex gap-2">
              {socialLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={link.ariaLabel}
                  className="transition-transform hover:scale-110 flex items-center justify-center w-8 h-8 rounded-full"
                  style={{ 
                    backgroundColor: link.bgColor,
                    color: link.iconColor,
                    border: link.borderColor ? `1px solid ${link.borderColor}` : 'none'
                  }}
                >
                  {link.icon}
                </a>
              ))}
            </div>
          </div>
          <div className="text-center text-xs w-full">
            {t('footer.copyright', { currentYear })}
          </div>
        </div>
      )}

      {/* Z Fold 6 and Tablet layout: Desktop-style footer (copyright left, social right) */}
      {isTablet && (
        <div className="flex justify-between items-center w-full py-2">
          <div className="text-sm flex-shrink-0 pl-6">
            {t('footer.copyright', { currentYear })}
          </div>
          <div className="flex items-center gap-4 flex-shrink-0 pr-6">
            <div className="flex gap-3">
              {socialLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={link.ariaLabel}
                  className="transition-transform hover:scale-110 flex items-center justify-center w-8 h-8 rounded-full"
                  style={{ 
                    backgroundColor: link.bgColor,
                    color: link.iconColor,
                    border: link.borderColor ? `1px solid ${link.borderColor}` : 'none'
                  }}
                >
                  {link.icon}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Desktop layout: Full desktop footer */}
      {isDesktop && (
        <div className="flex justify-between items-center w-full py-2">
          <div className="text-sm flex-shrink-0 pl-6">
            {t('footer.copyright', { currentYear })}
          </div>
          <div className="flex items-center gap-4 flex-shrink-0 pr-6">
            <div className="flex gap-3">
              {socialLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={link.ariaLabel}
                  className="transition-transform hover:scale-110 flex items-center justify-center w-8 h-8 rounded-full"
                  style={{ 
                    backgroundColor: link.bgColor,
                    color: link.iconColor,
                    border: link.borderColor ? `1px solid ${link.borderColor}` : 'none'
                  }}
                >
                  {link.icon}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};
